import get from 'lodash.get';
import { getButtonCellTextClassNames } from './get-class-names';
import { useMemo } from 'react';
import {
  getTitleKeyToUse,
  mapBreakpointToClassNames,
  mapResponsiveStylesToArray,
} from './utils';
import clsx from 'clsx';
import { getTextDecoration } from '~/utils/styles';

const TitleComponent = ({
  entry,
  selected,
  keyToUse,
  enabled,
  className,
  stateClassNames,
}: {
  entry: any;
  selected: boolean;
  keyToUse: string;
  enabled: boolean;
  className: string;
  stateClassNames: { default: string; selected: string };
}) => {
  const content = get(entry, keyToUse, '');
  return (
    enabled && (
      <span
        className={clsx(
          className,
          selected ? stateClassNames.selected : stateClassNames.default
        )}
      >
        <span className="block max-w-full truncate">{content}</span>
      </span>
    )
  );
};

type TitleProps = {
  selected: boolean;
  styles: any;
  entry: any;
};

export const Title = ({ styles: stylesProp, selected, entry }: TitleProps) => {
  const className = getButtonCellTextClassNames(selected);

  const components = useMemo(
    () =>
      mapResponsiveStylesToArray<any, React.FC<any>>(
        stylesProp,
        (styles, key) => {
          const titleSwitchEnabled = get(styles, 'title_switch', false);

          const textTransform = get(styles, 'title_text_transform', '');

          const textDecoration = getTextDecoration({
            textDecoration: get(styles, 'title_text_decoration', '') as string,
            hoverTextDecoration: get(
              styles,
              'title_text_decoration_hover',
              ''
            ) as string,
          });

          const textDecorationSelected = getTextDecoration({
            textDecoration: get(
              styles,
              'title_text_decoration_selected',
              ''
            ) as string,
            hoverTextDecoration: get(
              styles,
              'title_text_decoration_selected_hover',
              ''
            ) as string,
          });

          const keyToUse = getTitleKeyToUse(styles);

          // eslint-disable-next-line react/display-name
          return ({
            entry,
            selected,
            className,
          }: {
            entry: any;
            selected: boolean;
            className: string;
          }) => (
            <TitleComponent
              className={clsx(
                className,
                textTransform,
                mapBreakpointToClassNames(key) + 'flex',
                'min-w-0'
              )}
              stateClassNames={{
                default: textDecoration,
                selected: textDecorationSelected,
              }}
              entry={entry}
              selected={selected}
              keyToUse={keyToUse}
              enabled={titleSwitchEnabled}
            />
          );
        }
      ),
    [stylesProp]
  );

  return (
    <span className="flex min-w-0 overflow-hidden">
      {components.map((Component, index) => (
        <Component
          key={index}
          entry={entry}
          selected={selected}
          className={className}
        />
      ))}
    </span>
  );
};
